import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Snowflake,
  Search,
  Sparkles,
  ShieldCheck,
  Clock,
  Wrench,
  Wind,
  Gauge,
  Droplets,
  Phone,
  Mail,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PILLARS = [
  {
    icon: Search,
    title: "Diagnosztika & Javítás",
    text:
      "Nemcsak töltünk, hanem látunk is. Modern műszerekkel tárjuk fel a szivárgásokat, kompresszor- és nyomáshibákat — okot keresünk, nem tüneteket kezelünk.",
  },
  {
    icon: Sparkles,
    title: "Higiénia felsőfokon",
    text:
      "Ózonos fertőtlenítéssel és pollenszűrő cserével eltüntetjük a baktériumokat és a kellemetlen szagokat — friss, allergén-mentes utastér.",
  },
  {
    icon: ShieldCheck,
    title: "Garanciális munkavégzés",
    text:
      "Minden javításra és beépített alkatrészre teljes körű garanciát vállalunk. Ha a hiba visszatér, mi orvosoljuk.",
  },
  {
    icon: Clock,
    title: "Gyorsaság",
    text:
      "Tudjuk, hogy az ideje drága — előre egyeztetett időpontban, várakozás nélkül fogadjuk. Klímatöltés átlagosan 30–60 perc alatt.",
  },
];

const SERVICES = [
  {
    icon: Snowflake,
    title: "Klímatöltés (R134a és R1234yf)",
    text:
      "Precíz, gyári értékek szerinti feltöltés — vákuumozás, mennyiségi adagolás grammra pontosan, mindkét gáztípussal.",
  },
  {
    icon: Gauge,
    title: "Nyomáspróba és szivárgáskeresés",
    text:
      "UV-festékkel vagy nitrogénes nyomáspróbával a legkisebb repedést is megtaláljuk — nem küldjük csak úgy hűtéssel haza.",
  },
  {
    icon: Wrench,
    title: "Kompresszor felújítás és javítás",
    text:
      "Ha már nem csak a gáz a baj. Kompresszor cseréje vagy felújítása, mágneskapcsoló és vezérlés javítás.",
  },
  {
    icon: Wind,
    title: "Klímatisztítás (vegyszer / ózon)",
    text:
      "Vegyszeres elpárologtató-tisztítás vagy teljes ózonos fertőtlenítés — egészséges, friss levegő és hosszabb klíma-élettartam.",
  },
  {
    icon: Droplets,
    title: "Pollenszűrő csere",
    text:
      "Eredeti vagy gyári minőségű aktívszenes pollenszűrő csere — szezononként ajánlott, allergiásoknak kötelező.",
  },
  {
    icon: AlertCircle,
    title: "Hibakód-olvasás (klíma vezérlés)",
    text:
      "Klíma vezérlőegység diagnosztika ISTA / Launch / Bosch rendszerrel — ventilátor, hőmérőszenzor és aktuátor hibák feltárása.",
  },
];

const PRICES = [
  {
    range: "max. 500 g",
    price: "14 900 Ft",
    desc: "Kisebb autók (hatchback, kompakt szedán) — alap újratöltés.",
    popular: false,
  },
  {
    range: "500 – 750 g",
    price: "19 900 Ft",
    desc: "Közepes méretű személyautók, kisebb SUV-ok.",
    popular: true,
  },
  {
    range: "750 – 1000 g",
    price: "24 900 Ft",
    desc: "Nagyobb SUV-ok, kombi modellek, prémium szegmens.",
    popular: false,
  },
  {
    range: "1000 g felett",
    price: "Egyedi",
    desc: "Egyedi elbírálás alapján — hívjon, és pontos árajánlatot adunk.",
    popular: false,
  },
];

const FAQ = [
  {
    q: "Mikor kell klímát tölteni?",
    a: "Általánosságban 2–3 évente, vagy ha a hűtés érezhetően gyengébb. A rendszer apránként, természetesen veszít a gáz mennyiségéből — ez normális, nem feltétlenül jelent szivárgást.",
  },
  {
    q: "Honnan tudom, hogy az autóm R134a-t vagy R1234yf-et használ?",
    a: "Általában a motortérben lévő matrica jelzi, illetve a forgalmi engedély évjárata is irányadó: a 2017 utáni új autók többsége már R1234yf gázt használ. A két gáz ára eltér nálunk: R134a fix csomagáron (14 900 Ft-tól), R1234yf pedig 100 Ft/gramm egységáron, fogyasztásarányosan számolva.",
  },
  {
    q: "Miért fontos a klímatisztítás?",
    a: "A párologtatóra rakódott baktériumok és gombák okozzák a kellemetlen szagot, és allergiás tüneteket válthatnak ki. Ózonos fertőtlenítéssel ezeket maradéktalanul eltüntetjük.",
  },
  {
    q: "Mennyi időt vesz igénybe egy komplett klímaszerviz?",
    a: "Egy alap klímatöltés 30–60 perc. Ha tisztítás, pollenszűrő csere és diagnosztika is kell, kb. 1,5–2 órát szánjunk rá.",
  },
];

export default function KlimaPage() {
  const [lightboxSrc, setLightboxSrc] = React.useState(null);

  const closeLightbox = React.useCallback(() => setLightboxSrc(null), []);

  React.useEffect(() => {
    if (!lightboxSrc) return;
    const handler = (e) => e.key === "Escape" && closeLightbox();
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightboxSrc, closeLightbox]);

  useDocumentMeta({
    title:
      "Autóklíma szerviz Debrecen — Klímatöltés, javítás, fertőtlenítés | Autó-Szerelem",
    description:
      "Profi autóklíma szerviz Debrecenben. R134a és R1234yf gázzal való klímatöltés, szivárgáskeresés, kompresszor javítás, ózonos fertőtlenítés és pollenszűrő csere. Akciós áraink 14 900 Ft-tól. Időpontfoglalás online vagy telefonon: +36 30 863 7476.",
    path: "/klima",
  });

  // Klíma-specific JSON-LD (Service + FAQPage) for richer Google snippets
  React.useEffect(() => {
    const existing = document.getElementById("klima-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "klima-jsonld";
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Autóklíma szerviz",
        name: "Autóklíma javítás és töltés Debrecenben",
        provider: {
          "@type": "AutoRepair",
          name: "Autó-Szerelem — Séra Autószervíz",
          telephone: "+36-30-863-7476",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Vadvirág utca 15/D — 3. kapu",
            addressLocality: "Debrecen",
            postalCode: "4031",
            addressCountry: "HU",
          },
        },
        areaServed: { "@type": "City", name: "Debrecen" },
        offers: PRICES.map((p) => ({
          "@type": "Offer",
          name: `Klímatöltés (${p.range})`,
          priceCurrency: "HUF",
          price: p.price === "Egyedi" ? "0" : p.price.replace(/\s|Ft/g, ""),
          description: p.desc,
        })),
        url: "https://autoszerelem.hu/klima",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("klima-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="klima-page">
      <Navbar />

      {/* HERO */}
      <section
        className="relative pt-24 sm:pt-28 isolate overflow-hidden"
        data-testid="klima-hero-section"
      >
        {/* Cold blue gradient background */}
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at top right, rgba(0,130,210,0.45) 0%, rgba(10,10,10,0) 55%), radial-gradient(ellipse at 20% 60%, rgba(90,64,128,0.25) 0%, rgba(10,10,10,0) 50%), #0A0A0A",
            }}
          />
          {/* Frost / snow pattern */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 22px), repeating-linear-gradient(-45deg, #fff 0 1px, transparent 1px 22px)",
            }}
          />
          {/* Big ghost snowflake */}
          <Snowflake
            className="absolute -right-16 -top-10 w-[40rem] h-[40rem] text-[#0082D2]/[0.05] hidden lg:block"
            strokeWidth={0.6}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16 sm:pt-6 sm:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-6 transition-colors"
            data-testid="klima-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Autóklíma részleg / Debrecen
            </span>
          </div>

          <h1
            className="font-black uppercase leading-[0.92] tracking-[-0.01em] text-white max-w-5xl"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.1rem, 7vw, 6.5rem)",
            }}
            data-testid="klima-headline"
          >
            Szauna helyett{" "}
            <span className="text-[#0082D2]">jégverem</span>.
            <br />
            Profi autóklíma szerviz Debrecenben.
          </h1>

          <p
            className="mt-8 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed"
            data-testid="klima-subheadline"
          >
            Ne hagyja, hogy a hőség átvegye az irányítást az utastérben.
            Gyors, precíz és garantáltan hűvös megoldások — minden
            autótípushoz, R134a és R1234yf gázzal egyaránt.
          </p>

          <p
            className="mt-5 max-w-2xl text-zinc-400 text-sm sm:text-base leading-relaxed"
            data-testid="klima-location-seo"
          >
            Autóklíma szervizünk Debrecenben, a Kishegyesi út és a Kishatár úti
            Auchan áruház közvetlen közelében, a Vadvirág utcában található,
            így a nyugati ipari övezetből és a Határ Úti Ipari Parkból is
            percek alatt elérhető.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/?service=Kl%C3%ADma%20jav%C3%ADt%C3%A1s-t%C3%B6lt%C3%A9s#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="klima-cta-booking"
            >
              Klíma időpontfoglalás <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="klima-cta-phone"
            >
              <Phone className="w-4 h-4" /> +36 30 863 7476
            </a>
          </div>

          {/* Quick facts strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { k: "14 900 Ft-tól", v: "Klímatöltés" },
              { k: "30–60 perc", v: "Átlagos időtartam" },
              { k: "R134a + R1234yf", v: "Mindkét gáz" },
              { k: "100% garancia", v: "Minden munkára" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-[#0d0d0d] px-5 py-5"
                data-testid={`klima-fact-${i}`}
              >
                <div
                  className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.k}
                </div>
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — Pillars */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="klima-pillars-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Miért válasszon minket?</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Nem csak töltjük —
                <br />
                <span className="text-[#0082D2]">megoldjuk.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              4 dolog, amiben máshogy dolgozunk, mint a "gyorsklíma" cégek.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="bg-[#0d0d0d] hover:bg-[#121212] transition-colors p-7"
                  data-testid={`klima-pillar-${i}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/30 mb-6">
                    <Icon
                      className="w-6 h-6 text-[#0082D2]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES grid */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="klima-services-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Szolgáltatásaink</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Minden, ami a klímához
                <br />
                <span className="text-[#0082D2]">tartozik.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="service-card flex flex-col"
                  data-testid={`klima-service-${i}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/30">
                      <Icon
                        className="w-6 h-6 text-[#0082D2]"
                        strokeWidth={1.5}
                      />
                    </div>
                    <span
                      className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-500"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      0{i + 1}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CASE STUDY — Kondenzátor csere (Előtte / Utána) */}
      <section
        id="klima-munkaink"
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="klima-case-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span
                className="text-[0.7rem] font-bold uppercase tracking-[0.32em] text-[#0082D2]"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Munkáink — Esettanulmány
              </span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Kondenzátor csere
                <br />
                <span className="text-[#0082D2]">előtte / utána.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              A klímahűtő (kondenzátor) az autó orránál található — folyamatos
              kőfelverődés, sár, út-só és por terheli. Egy elhasznált
              kondenzátor egyszerűen képtelen hűteni, hiába van benne gáz.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
            {/* BEFORE — old worn condenser */}
            <button
              type="button"
              onClick={() => setLightboxSrc("/case-studies/klima/klima-elhasznalt-kondenzator.jpg")}
              className="group relative aspect-[4/3] bg-[#101010] border border-white/5 hover:border-[#E1132C]/50 overflow-hidden text-left transition-all cursor-zoom-in"
              data-testid="klima-case-before"
              aria-label="Elhasznált kondenzátor — nagyítás"
            >
              <img
                src="/case-studies/klima/klima-elhasznalt-kondenzator.jpg"
                alt="Elhasznált autóklíma kondenzátor — eltömődött, korrodált bordák"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30" />
              <div className="absolute top-5 left-5">
                <span
                  className="inline-flex items-center gap-2 bg-[#E1132C]/95 text-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Előtte
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <h3
                  className="font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)",
                  }}
                >
                  Elhasználódott, eltömődött kondenzátor
                </h3>
                <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-md">
                  Évek alatt a légből érkező szennyeződés rárakódik a vékony
                  bordákra, korrodálnak az alumínium lamellák. A levegő nem
                  tud átáramolni — a klíma még friss gázzal sem hűt.
                </p>
              </div>
            </button>

            {/* AFTER — new condenser installed */}
            <button
              type="button"
              onClick={() => setLightboxSrc("/case-studies/klima/klima-uj-kondenzator.jpg")}
              className="group relative aspect-[4/3] bg-[#101010] border border-white/5 hover:border-[#0082D2]/60 overflow-hidden text-left transition-all cursor-zoom-in"
              data-testid="klima-case-after"
              aria-label="Új kondenzátor beépítve — nagyítás"
            >
              <img
                src="/case-studies/klima/klima-uj-kondenzator.jpg"
                alt="Új autóklíma kondenzátor beszerelve a motortérbe"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30" />
              <div className="absolute top-5 left-5">
                <span
                  className="inline-flex items-center gap-2 bg-[#0082D2]/95 text-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Utána
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <h3
                  className="font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)",
                  }}
                >
                  Új kondenzátor beszerelve
                </h3>
                <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-md">
                  Eredeti minőségű alkatrész, vákuumozás, szárító csere és
                  precíz töltés gyári mennyiségre. A klíma azonnal hűt — és
                  hűteni is fog hosszú éveken át.
                </p>
              </div>
            </button>
          </div>

          {/* Process strip */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { n: "01", t: "Diagnosztika", d: "Hibaok feltárása nyomáspróbával." },
              { n: "02", t: "Bontás", d: "Lökhárító, légszűrő, csövek leszerelése." },
              { n: "03", t: "Csere", d: "Új kondenzátor + szárító + tömítések." },
              { n: "04", t: "Töltés & teszt", d: "Vákuum, gáz, hűtési próba." },
            ].map((p, i) => (
              <div
                key={i}
                className="bg-[#0d0d0d] p-6"
                data-testid={`klima-case-step-${i}`}
              >
                <div
                  className="text-[#0082D2] text-2xl font-black tracking-tight"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.n}
                </div>
                <h4
                  className="mt-3 text-base font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.t}
                </h4>
                <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-zinc-500 max-w-3xl">
            * Hasonló problémát észlel? Ha autója klímája rosszul hűt vagy a
            motortér eleje láthatóan eltömődött, jó eséllyel a kondenzátor a
            ludas — diagnosztikára várjuk.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="klima-arak"
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="klima-pricing-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Árlista</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Klímatöltés
                <br />
                <span className="text-[#0082D2]">átlátható áron.</span>
              </h2>

              {/* Gas type indicator */}
              <div
                className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 border border-[#0082D2]/40 bg-[#0082D2]/10"
                data-testid="klima-gas-r134a-badge"
              >
                <span className="w-2 h-2 rounded-full bg-[#0082D2]" />
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.22em] text-[#0082D2]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Az alábbi árak R134a gázra érvényesek
                </span>
              </div>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              Az ár a feltöltött gáz mennyiségétől függ. Diagnosztika és
              szivárgásvizsgálat egyedi árazás alapján — kérje ajánlatunkat!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRICES.map((p, i) => (
              <div
                key={i}
                className={`relative bg-[#0d0d0d] border ${
                  p.popular
                    ? "border-[#0082D2]/60"
                    : "border-white/10"
                } p-7 flex flex-col`}
                data-testid={`klima-price-${i}`}
              >
                {p.popular && (
                  <span
                    className="absolute -top-3 left-7 bg-[#0082D2] text-white text-[0.6rem] font-black uppercase tracking-[0.24em] px-3 py-1"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    Leggyakoribb
                  </span>
                )}
                <div
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em] text-zinc-500"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Töltet
                </div>
                <div
                  className="mt-1 text-2xl font-black uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.range}
                </div>

                <div className="mt-6 pb-6 border-b border-white/10">
                  <div
                    className="text-4xl font-black text-[#0082D2] tracking-tight"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.price}
                  </div>
                </div>

                <p className="mt-5 text-sm text-zinc-400 leading-relaxed flex-1">
                  {p.desc}
                </p>

                <Link
                  to="/?service=Kl%C3%ADma%20jav%C3%ADt%C3%A1s-t%C3%B6lt%C3%A9s#foglalas"
                  className="mt-6 inline-flex items-center justify-center px-4 py-3 border border-white/10 hover:border-[#0082D2] hover:bg-[#0082D2]/10 text-white text-xs font-bold uppercase tracking-[0.18em] transition-colors"
                  style={{ fontFamily: "Barlow Condensed" }}
                  data-testid={`klima-price-cta-${i}`}
                >
                  Időpontot kérek <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Link>
              </div>
            ))}
          </div>

          {/* R1234yf — separate banner with per-gram pricing */}
          <div
            className="mt-6 relative overflow-hidden bg-[#0d0d0d] border border-white/10 p-7 sm:p-9"
            data-testid="klima-pricing-yf-banner"
            style={{
              background:
                "linear-gradient(135deg, rgba(0,130,210,0.10) 0%, #0d0d0d 55%)",
            }}
          >
            <div className="m-stripe absolute top-0 left-0 right-0 h-[2px]" />
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 border border-[#0082D2]/40 bg-[#0082D2]/10">
                  <span className="w-2 h-2 rounded-full bg-[#0082D2]" />
                  <span
                    className="text-[0.7rem] font-bold uppercase tracking-[0.22em] text-[#0082D2]"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    R1234yf gáz — újabb autókhoz
                  </span>
                </div>
                <h3
                  className="font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.75rem, 3.6vw, 2.5rem)",
                  }}
                >
                  R1234yf klímatöltés
                </h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-xl">
                  A 2017 utáni új autók többsége már R1234yf gázt használ
                  (kötelező gyári előírás). Ennek a gáznak az ára magasabb,
                  ezért nálunk grammra pontosan, fogyasztásarányosan számoljuk.
                </p>
              </div>
              <div className="md:col-span-5 md:text-right">
                <div className="inline-flex flex-col items-start md:items-end gap-1 bg-white/95 text-[#0A0A0A] px-5 py-4">
                  <span
                    className="text-[0.7rem] font-bold uppercase tracking-[0.22em] text-zinc-600 leading-none"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    Egységár
                  </span>
                  <span
                    className="font-black tracking-tight leading-none"
                    style={{
                      fontFamily: "Barlow Condensed",
                      fontSize: "clamp(2.25rem, 4.4vw, 3.2rem)",
                    }}
                  >
                    100 Ft<span className="text-zinc-600">/g</span>
                  </span>
                </div>
                <div className="mt-4">
                  <Link
                    to="/?service=Kl%C3%ADma%20jav%C3%ADt%C3%A1s-t%C3%B6lt%C3%A9s#foglalas"
                    className="btn-outline inline-flex items-center"
                    data-testid="klima-yf-cta"
                  >
                    Ajánlatkérés <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-zinc-500 max-w-3xl">
            * Az árak tartalmazzák a vákuumozást, gáz feltöltését gyári
            mennyiségre, és a rendszer ellenőrzését. A tüntetett árak
            irányárak — egyedi munkák (kompresszor csere, súlyos szivárgás
            javítás) külön kalkulációt igényelnek.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="klima-faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="section-eyebrow mb-4">GYIK</span>
          <h2
            className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95] mb-12"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Gyakori kérdések.
          </h2>

          <div className="space-y-px bg-white/5 border border-white/5">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="group bg-[#0d0d0d] hover:bg-[#121212] transition-colors"
                data-testid={`klima-faq-${i}`}
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="text-white font-semibold pr-4">{f.q}</span>
                  <span className="w-7 h-7 flex items-center justify-center border border-white/20 text-[#0082D2] text-lg leading-none transition-transform group-open:rotate-45 shrink-0">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="klima-cta-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] border border-white/10 p-10 sm:p-14 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#0082D2]/25 blur-3xl pointer-events-none" />
            <div className="m-stripe absolute top-0 left-0 right-0 h-1" />
            <Snowflake
              className="absolute -right-10 bottom-[-20px] w-64 h-64 text-[#0082D2]/[0.06] hidden md:block"
              strokeWidth={0.7}
            />

            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em] text-[#0082D2]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Ne várja meg a kánikulát!
                </span>
                <h3
                  className="mt-3 font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                  }}
                >
                  Készítse fel autóját
                  <br />
                  a szezonra még ma.
                </h3>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Kérjen időpontot online vagy telefonon, és élvezze a friss,
                  hűvös levegőt minden kilométeren.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <Link
                  to="/?service=Kl%C3%ADma%20jav%C3%ADt%C3%A1s-t%C3%B6lt%C3%A9s#foglalas"
                  className="btn-primary inline-flex items-center w-full md:w-auto justify-center"
                  data-testid="klima-cta-banner-booking"
                >
                  Időpontot foglalok <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-outline inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  data-testid="klima-cta-banner-phone"
                >
                  <Phone className="w-4 h-4" /> Hívás most
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
            <a
              href="tel:+36308637476"
              className="bg-[#0e0e0e] border border-white/5 hover:border-[#0082D2]/40 transition-colors p-5"
              data-testid="klima-contact-phone"
            >
              <Phone className="w-5 h-5 text-[#0082D2] mx-auto mb-2" strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Telefon
              </div>
              <div className="text-white font-medium mt-1">+36 30 863 7476</div>
            </a>
            <a
              href="mailto:autoszerelem@gmail.com"
              className="bg-[#0e0e0e] border border-white/5 hover:border-[#0082D2]/40 transition-colors p-5"
              data-testid="klima-contact-email"
            >
              <Mail className="w-5 h-5 text-[#0082D2] mx-auto mb-2" strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                E-mail
              </div>
              <div className="text-white font-medium mt-1">autoszerelem@gmail.com</div>
            </a>
            <div className="bg-[#0e0e0e] border border-white/5 p-5">
              <CheckCircle2 className="w-5 h-5 text-[#0082D2] mx-auto mb-2" strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Cím
              </div>
              <div className="text-white font-medium mt-1">
                4031 Debrecen, Vadvirág u. 15/D
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Lightbox for klíma case study photos */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          data-testid="klima-case-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Bezárás"
            data-testid="klima-case-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
